/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Table } from '@mantine/core'
import Pagination from '../../../../client/js/components/pagination/Pagination'
import PaginationProvider from '../../../../client/js/components/pagination/PaginationProvider'
import NoData from '../../../../client/js/components/NoData'
import {
  TRANSACTION_RENTAL,
  TRANSACTION_SALE,
  TRANSACTION_SEASONAL_RENTAL
} from '../../enums/TransactionCodes.mjs'
import {
  mapProspectingAndCityToCityId,
  mapProspectingAndEstateTypeToType
} from '../../lib/mapping.mjs'
import { findManyEstates } from '../../lib/rest/estateAPIClient'
import ProspectingResultListItem from './ProspectingResultListItem'
import usePaginationQuery from '../../../../client/js/components/pagination/usePaginationQuery'
import { useQuery } from '@tanstack/react-query'
import { getHalEmbedded, getHalPageTotalElements } from '@jalik/hal'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'

export type ProspectingResultListProps = {
  prospecting: Partial<Prospecting>
}

function ProspectingResultList (props: ProspectingResultListProps) {
  const { prospecting } = props

  const filters = useMemo(() => ({
    bedrooms: prospecting.bedrooms,
    cities: prospecting.cities?.map(mapProspectingAndCityToCityId).join(','),
    equipped: prospecting.equipped,
    furnished: prospecting.furnished,
    maxPrice: prospecting.maxPrice,
    minPrice: prospecting.minPrice,
    transaction: prospecting.transaction,
    types: prospecting.types?.map(mapProspectingAndEstateTypeToType).join(','),
    available: true,
    published: true
  }), [
    prospecting.bedrooms,
    prospecting.cities,
    prospecting.equipped,
    prospecting.furnished,
    prospecting.maxPrice,
    prospecting.minPrice,
    prospecting.transaction,
    prospecting.types
  ])

  const [totalElements, setTotalElements] = useState<number>(0)
  const pagination = usePaginationQuery(totalElements)
  const { page, limit } = pagination

  const resultsQuery = useQuery({
    enabled: true,
    queryKey: ['prospectingResults', filters, page, limit],
    queryFn: () => findManyEstates(filters).then((r) => {
      setTotalElements(getHalPageTotalElements(r))
      return r
    })
  })
  const { data } = resultsQuery
  const items = useMemo(() => (
    (data != null ? getHalEmbedded(data)?.estates : null) || []
  ), [data])

  return (
    <LoadingHelper
      query={resultsQuery}
      render={() => (
        <>
          <hr />
          <PaginationProvider context={pagination}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={200}>
                    <FormattedMessage
                      id="photo"
                      defaultMessage="Photo"
                    />
                  </Table.Th>
                  <Table.Th>
                    <FormattedMessage
                      id="reference"
                      defaultMessage="Référence"
                    />
                  </Table.Th>
                  <Table.Th>
                    <FormattedMessage
                      id="title"
                      defaultMessage="Titre"
                    />
                  </Table.Th>
                  <Table.Th>
                    <FormattedMessage
                      id="city"
                      defaultMessage="Ville"
                    />
                  </Table.Th>
                  <Table.Th ta="end">
                    {prospecting.transaction === TRANSACTION_SALE || prospecting.transaction == null
                      ? <FormattedMessage
                        id="price_including_tax"
                        defaultMessage="Prix TTC"
                      />
                      : null}

                    {prospecting.transaction === TRANSACTION_RENTAL
                      ? <FormattedMessage
                        id="rent"
                        defaultMessage="Loyer"
                      />
                      : null}

                    {prospecting.transaction === TRANSACTION_SEASONAL_RENTAL
                      ? <FormattedMessage
                        id="bed_night"
                        defaultMessage="Nuitée"
                      />
                      : null}
                  </Table.Th>
                  <Table.Th>
                    <FormattedMessage
                      id="owner"
                      defaultMessage="Propriétaire"
                    />
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.length > 0
                  ? items.map((estate) => (
                    <ProspectingResultListItem
                      key={estate.id}
                      item={estate}
                    />
                  ))
                  : <Table.Tr>
                    <Table.Td colSpan={9}>
                      <NoData />
                    </Table.Td>
                  </Table.Tr>
                }
              </Table.Tbody>
            </Table>
            <Pagination />
          </PaginationProvider>
        </>
      )}
    />
  )
}

export default ProspectingResultList
